import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { getBackendUrl } from "@/lib/backend";
import {
  buildDataEntryLocalGuidance,
  buildDataEntryMetricsPayload,
} from "@/utils/buildDataEntryLocalGuidance";

const API = `${getBackendUrl()}/api`;

export function useDataEntryAiGuidance({
  overview,
  blockData,
  completionBands,
  criticalCount,
  criticalSchools,
  highRepeaterSchools,
  dataQuality,
  certificationData,
  topBottomBlocks,
  scopeLabel,
  scopeKey,
}) {
  const [guidance, setGuidance] = useState(null);
  const [provider, setProvider] = useState("local");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const requestIdRef = useRef(0);

  const buildLocal = useCallback(
    () =>
      buildDataEntryLocalGuidance({
        overview,
        blockData,
        completionBands,
        criticalCount,
        highRepeaterSchools,
        dataQuality,
        certificationData,
        topBottomBlocks,
      }),
    [
      overview,
      blockData,
      completionBands,
      criticalCount,
      highRepeaterSchools,
      dataQuality,
      certificationData,
      topBottomBlocks,
    ]
  );

  const fetchGuidance = useCallback(() => {
    if (!overview?.total_schools) {
      setGuidance(null);
      setProvider("local");
      setLoading(false);
      return Promise.resolve();
    }

    const localGuidance = buildLocal();
    const requestId = ++requestIdRef.current;
    const metrics = buildDataEntryMetricsPayload({
      overview,
      blockData,
      completionBands,
      criticalCount,
      criticalSchools,
      highRepeaterSchools,
      dataQuality,
      certificationData,
      topBottomBlocks,
      scopeLabel,
    });

    setLoading(true);
    setError(null);

    return axios
      .post(`${API}/data-entry/ai-guidance`, { metrics, local_guidance: localGuidance })
      .then((res) => {
        if (requestId !== requestIdRef.current) return;
        if (res.data?.guidance) {
          setGuidance(res.data.guidance);
          setProvider(res.data.provider || "local");
          setGeneratedAt(res.data.generated_at || new Date().toISOString());
        }
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        setError(err?.response?.data?.detail || err.message || "AI guidance unavailable");
        setGuidance(localGuidance);
        setProvider("local");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      });
  }, [
    overview,
    blockData,
    completionBands,
    criticalCount,
    criticalSchools,
    highRepeaterSchools,
    dataQuality,
    certificationData,
    topBottomBlocks,
    scopeLabel,
    buildLocal,
  ]);

  useEffect(() => {
    fetchGuidance();
  }, [scopeKey, fetchGuidance]);

  return {
    guidance,
    provider,
    loading,
    error,
    generatedAt,
    refresh: fetchGuidance,
    isMistral: provider === "mistral",
  };
}
